import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  Output,
  Signal,
} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {MatIcon} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatTooltip} from '@angular/material/tooltip';
import {Router, RouterLink} from '@angular/router';
import {ManagedCampaignsStore} from '@em/campaigns/data-access';
import {
  DownloadGroupProductsService,
  findMatchingCampaigns,
  GroupsProductsService,
  IAppliedCampaign,
  ProductGroupsFacade,
  ProductsCountService,
} from '@em/data-feed/data-access-products';
import {
  ProductGroupEntity,
  ProductGroupFilterLogicalCondition,
} from '@em/data-feed/util-types';
import {ConditionalStatementList} from '@em/shared/mapping-engine/domain-models';
import {
  FACEBOOK,
  GOOGLE,
  MICROSOFT,
  PlatformId,
} from '@em/shared/platforms/util';
import {
  DialogService,
  EmBoxModule,
  EmButtonModule,
  EmIconButtonComponent,
  EmIconModule,
  EmMoreInfoModule,
  EmSpinnerModule,
  EmTagComponent,
  EmThreeDotsLoadingModule,
} from '@em/shared/ui';
import {isNotNullOrUndefined} from '@em/shared/util-rxjs';
import {ContractService} from '@em/subscription/data-access';
import {UserService} from '@em/user-account/data-access';
import {TranslateModule} from '@ngx-translate/core';
import {EMPTY, Observable} from 'rxjs';
import {catchError, map, switchMap, take, tap} from 'rxjs/operators';
import {EmProductGroupNamePipe} from '../../pipes/product-group-name/product-group-name.pipe';
import {EmReadableFiltersComponent} from '../readable-filters/readable-filters.component';
import {HttpErrorResponse} from '@angular/common/http';
import {GroupDialogService} from '../../services/group-dialog/group-dialog.service';

const logoMapper: {[k: string]: string} = {
  'google-shopping': GOOGLE.logos.campaign,
  facebook: FACEBOOK.logos.general,
  bing: MICROSOFT.logos.general,
};

@Component({
  selector: 'em-product-group-card',
  imports: [
    CommonModule,
    TranslateModule,
    RouterLink,
    MatMenuModule,
    EmIconButtonComponent,
    EmMoreInfoModule,
    EmButtonModule,
    EmIconModule,
    EmThreeDotsLoadingModule,
    EmSpinnerModule,
    EmProductGroupNamePipe,
    EmReadableFiltersComponent,
    EmBoxModule,
    EmTagComponent,
    MatIcon,
    MatTooltip,
  ],
  templateUrl: './product-group-card.component.html',
  styleUrls: ['./product-group-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmProductGroupCardComponent implements OnInit {
  private readonly _dialog = inject(DialogService);
  private readonly _productsCountService = inject(ProductsCountService);
  private readonly _router = inject(Router);
  private readonly _groupDialogService = inject(GroupDialogService);
  private readonly _productGroupsFacade = inject(ProductGroupsFacade);
  private readonly _groupProductsService = inject(GroupsProductsService);
  private readonly _contractService = inject(ContractService);
  private readonly _downloadProductsService = inject(
    DownloadGroupProductsService,
  );
  private readonly _managedCampaignsStore = inject(ManagedCampaignsStore, {
    optional: true,
  });

  group = input<ProductGroupEntity | null | undefined>();

  @Input() isSelected?: boolean;
  @Input() allowDelete?: boolean = true;
  @Input() allowEdit?: boolean = true;
  @Input() allowDuplicate?: boolean = true;
  @Input() compactView = false;
  @Input() showTags = false;
  @Output() updated = new EventEmitter<ProductGroupEntity | string>();

  private readonly _userService = inject(UserService);

  totalProductsCount = toSignal(this._productsCountService.productsCount$);
  groupProductsCount = toSignal(
    toObservable(this.group).pipe(
      isNotNullOrUndefined(),
      switchMap((group) => this._groupProductsService.getProductsIds(group)),
      map((products) => products.length),
    ),
  );
  productPercentage = computed(() => {
    const total = this.totalProductsCount();
    const groupCount = this.groupProductsCount();
    if (!total) {
      return 0;
    }
    return (groupCount || 0) / total;
  });
  isLoadingCampaigns$: Observable<boolean> | undefined;
  appliedCampaigns$: Observable<IAppliedCampaign[]> | undefined;
  isLoadingCount$ = toObservable(this.group).pipe(
    isNotNullOrUndefined(),
    switchMap((group) => this._groupProductsService.isLoading(group?.uuid)),
  );
  conditionalStatementList: Signal<
    ConditionalStatementList<ProductGroupFilterLogicalCondition> | undefined
  > = computed(() => this.group()?.conditionalStatementList);

  get appliedCampaigns() {
    if (!this.group()) return [];
    return this.group()?.appliedCampaigns || [];
  }

  ngOnInit(): void {
    if (this._managedCampaignsStore) {
      this.isLoadingCampaigns$ = this._managedCampaignsStore.isLoadingAny$;
      this.appliedCampaigns$ = this._managedCampaignsStore.allCampaigns$.pipe(
        map((allCampaigns) =>
          findMatchingCampaigns(
            this.group()?.appliedCampaigns || [],
            allCampaigns,
          ),
        ),
      );
    }
  }

  isAdmin(): boolean {
    return this._userService.isAdmin();
  }

  platformIcon(platformKey: PlatformId | undefined): string {
    return platformKey ? logoMapper[platformKey] : '';
  }

  editGroup(productGroup: ProductGroupEntity) {
    // if no uuid proviced don't automatically save, emit in the output
    if (productGroup.uuid) {
      this._groupDialogService
        .openEditGroupAndSave(productGroup.country, productGroup)
        .pipe(
          tap((updated) => {
            if (updated) {
              this.updated.emit(updated);
            }
          }),
        )
        .subscribe();
    } else {
      this._groupDialogService
        .openAddEditGroup(productGroup)
        .pipe(
          tap((updated) => {
            if (updated) {
              this.updated.emit(updated);
            }
          }),
        )
        .subscribe();
    }
  }

  deleteGroup(productGroup: ProductGroupEntity) {
    if (productGroup.uuid) {
      this._contractService.editableContract$
        .pipe(
          take(1),
          tap((contract) => {
            const crawlGroups = contract?.price_crawls?.map(
              (crawl) => crawl.selected_filters_uuid,
            );
            if (productGroup.uuid && crawlGroups?.includes(productGroup.uuid)) {
              this._informCantDelete();
            } else {
              this._confirmAndDelete(productGroup);
            }
          }),
        )
        .subscribe();
    }
  }

  toProducts(group: ProductGroupEntity) {
    this._router.navigate(['/', 'products', 'list'], {
      queryParams: {group: group.uuid},
    });
  }

  duplicateGroup(productGroup: ProductGroupEntity) {
    this._groupDialogService
      .duplicateAndEditGroup(productGroup)
      .pipe(
        tap((updated) => {
          if (updated) {
            this.updated.emit(updated);
          }
        }),
      )
      .subscribe();
  }

  downloadProductsCSV(productGroup: ProductGroupEntity) {
    if (productGroup.uuid) {
      this._downloadProductsService
        .downloadGroupProducts(productGroup.uuid, productGroup.name)
        .subscribe();
    }
  }

  private _confirmAndDelete(productGroup: ProductGroupEntity) {
    this._dialog
      .confirm({
        title: 'FILTER_SET_CONFIRM_DELETE_TITLE',
        description: 'FILTER_SET_CONFIRM_DELETE_TEXT',
        confirmLabel: 'SHARED_LABEL_DELETE',
        confirmType: 'danger',
      })
      .pipe(
        switchMap((confirmed) => {
          if (confirmed && productGroup.uuid) {
            return this._productGroupsFacade.deleteGroup(
              productGroup.uuid,
              productGroup.country,
            );
          } else {
            return EMPTY;
          }
        }),
        catchError((err: unknown) => {
          if ((err as HttpErrorResponse).status === 422) {
            return this._dialog.info(
              {
                title: 'DELETE_GROUP_NOT_POSSIBLE',
                description: 'DELETE_GROUP_USED_IN_EXCLUDE_GROUP',
              },
              {maxWidth: 500},
            );
          }

          return EMPTY;
        }),
      )
      .subscribe();
  }

  private _informCantDelete() {
    this._dialog
      .info({
        confirmType: 'danger',
        title: 'DELETE_GROUP_NOT_POSSIBLE',
        description: 'DELETE_GROUP_USED_IN_CRAWL_JOB',
      })
      .subscribe();
  }
}
